import React, { useEffect, useState } from 'react';
import { bool, func, string } from 'prop-types';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import _ from 'lodash';

import { connect } from 'react-redux';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';

import { Intervals, BacNetTypes, NiagaraTypes } from '../../helpers/profileObjectData';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as ProfileObjectActions from '../../actions/profileObjectActions';
import * as UiActions from '../../actions/uiActions';

import classes from './CreateEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    name: Yup.string()
        .max(45)
        .required(),
    dataType:     Yup.string(),
    showObixPath: Yup.boolean(),
    obixPath:     Yup.string()
        .when('showObixPath', {
            is:   true,
            then: Yup.string().required()
        }),
    objectType:         Yup.string().required(),
    showObjectInstance: Yup.boolean(),
    showPropertyId:     Yup.boolean(),
    objectInstance:     Yup.string()
        .when('showObjectInstance', {
            is:   true,
            then: Yup.string().required()
        }),
    showProprietaryObjectTypeId: Yup.boolean(),
    proprietaryObjectTypeId:     Yup.string()
        .test(
            'proprietaryObjectTypeId',
            'Proprietary Object Type Id must be between 128 and 1023',
            value => (_.toNumber(value) >= 128 && _.toNumber(value) <= 1023) || _.isEmpty(value),
        )
        .when('showProprietaryObjectTypeId', {
            is:   true,
            then: Yup.string().required()
        }),
    arrayIndex: Yup.string()
        .test(
            'arrayIndex',
            'Array Index must be between 0 and 1000',
            value => (_.toNumber(value) >= 0 && _.toNumber(value) <= 1000) || _.isEmpty(value),
        ),
    property:   Yup.string(),
    propertyId: Yup.string()
        .test(
            'propertyId',
            'Property ID must be equal to or greater than 0',
            value => _.toNumber(value) >= 0 || _.isEmpty(value),
        )
        .when(['showPropertyId', 'property'], {
            is:   (showPropertyId, property) => showPropertyId && property,
            then: Yup.string().required()
        }),
    trendInterval: Yup.string()
});

const DEFAULT_FORM_VALUES = {
    name:                        '',
    dataType:                    '',
    showObixPath:                false,
    obixPath:                    '',
    objectType:                  '',
    showObjectInstance:          false,
    showPropertyId:              false,
    showProprietaryObjectTypeId: false,
    objectInstance:              '',
    proprietaryObjectTypeId:     '',
    arrayIndex:                  '',
    property:                    '',
    propertyId:                  '',
    trendInterval:               ''
};

const InitialValues = {...DEFAULT_FORM_VALUES};

const EditProfileObjectModal = ({
    clearErrors,
    close,
    errors,
    isLoading,
    object,
    open,
    profiles,
    putProfileObject,
    selectedProfileId,
    title,
    toggleLoading,
    updateBacNetProfileObject,
    ...props
}) => {
    const [basType, setBasType] = useState();
    const [openArrayIndex,setOpenArrayIndex] = useState(false);
    const [propertySelected, setPropertySelected] = useState(false);

    useEffect(() => {
        if (open) {
            const targetProfile = _.find(profiles, profile => profile.id === selectedProfileId);

            setBasType(targetProfile.basType);
        } else {
            setBasType();
        }
    }, [open]);

    const formik = useFormik({
        initialValues:      InitialValues,
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting, resetForm }) => {
            let isUpdated, body;

            clearErrors();
            setSubmitting(true);

            switch (basType) {
                case 'Niagara':
                    body = {
                        changeOfValue:  convertToNumber(values.trendInterval) === 0,
                        dataType:       values.dataType,
                        objectType:     values.objectType.type,
                        oBIXPath:       values.obixPath,
                        name:           values.name,
                        sampleInterval: convertToNumber(values.trendInterval),
                        id:             _.get(object, 'id', '')
                    };

                    break;
                case 'BacnetIP':
                case 'BacnetEthernet':
                    body = {
                        arrayIndex:              _.isEmpty(values.arrayIndex.toString()) ? -1 : convertToNumber(values.arrayIndex),
                        changeOfValue:           convertToNumber(values.trendInterval) === 0,
                        dataType:                values.dataType,
                        name:                    values.name,
                        objectId:                convertToNumber(values.objectInstance),
                        objectType:              values.objectType.type,
                        proprietaryObjectTypeId: _.isEmpty(values.proprietaryObjectTypeId) ? -1 : convertToNumber(values.proprietaryObjectTypeId),
                        propertyId:              convertToNumber(values.propertyId),
                        sampleInterval:          convertToNumber(values.trendInterval) === 0 ? 15 : convertToNumber(values.trendInterval),
                        id:                      _.get(object, 'id', '')
                    };

                    break;
                default:
                    break;
            }

            try {
                if (!isLoading) {
                    toggleLoading(true);

                    switch (basType) {
                        case 'Niagara':
                            isUpdated = await putProfileObject(body);
                            break;
                        case 'BacnetIP':
                        case 'BacnetEthernet':
                            isUpdated = await updateBacNetProfileObject(selectedProfileId, body);
                            break;
                        default:
                            break;
                    }
                }

                if (isUpdated) {
                    setSubmitting(false);
                    toggleLoading(false);

                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    useEffect(() => {
        if (formik.values.objectType) {
            if (formik.values.objectType.type === 'Proprietary') {
                formik.setFieldValue('showProprietaryObjectTypeId', true, false);
                setOpenArrayIndex(true);
            }
            else if (isObjectTypeProprietary(formik.values.objectType.type)) {
                formik.setFieldValue('showProprietaryObjectTypeId', false, false);
                formik.setFieldValue('proprietaryObjectTypeId', '', false);
                setOpenArrayIndex(true);
            } 
            else {
                formik.setFieldValue('showProprietaryObjectTypeId', false, false);
                formik.setFieldValue('proprietaryObjectTypeId', '', false);
                formik.setFieldValue('arrayIndex', '', false);
                setOpenArrayIndex(false);
            }
        }
    }, [formik.values.objectType]);

    const isObjectTypeProprietary = objectType => {
        if (objectType === undefined ||
            objectType === 'AnalogInput' ||
            objectType === 'AnalogOutput' ||
            objectType === 'AnalogValue' ||
            objectType === 'BinaryInput' ||
            objectType === 'BinaryOutput' ||
            objectType === 'BinaryValue' ||
            objectType === 'MultiStateInput' ||
            objectType === 'MultiStateOutput' ||
            objectType === 'MultiStateVariable') {
            return false;
        }
        else {
            return true;
        }
    };

    const convertToNumber = input => {
        return _.toNumber(input);
    };

    const handleReset = () => {
        if (InitialValues.name.length) {
            Object.assign(InitialValues, DEFAULT_FORM_VALUES);
        }

        if (_.get(object, 'dataType', '') !== 'float') {
            if (Intervals[0] !== 0) {
                Intervals.unshift(0);
            }
        } else {
            _.remove(Intervals, value => value === 0);
        }

        const changeOfValue = _.get(object, 'changeOfValue', false);
        const dataType = _.get(object, 'dataType', '');

        InitialValues.name = _.get(object, 'name', '');
        InitialValues.dataType = dataType;
        InitialValues.trendInterval = changeOfValue && dataType !== 'float' ? 0 : _.get(object, 'sampleInterval', '');

        const objectType = _.get(object, 'objectType', '');

        let proprietaryId, arrayIndex;

        switch (basType) {
            case 'Niagara':
                InitialValues.obixPath = _.get(object, 'oBIXPath', '');
                InitialValues.objectType = _.find(NiagaraTypes, item => item.type === objectType);

                InitialValues.showObixPath = true;
                break;
            case 'BacnetIP':
            case 'BacnetEthernet':
                proprietaryId = _.get(object, 'proprietaryObjectTypeId', '');
                arrayIndex = _.get(object, 'arrayIndex', '');

                InitialValues.objectInstance = _.get(object, 'objectId', '');
                InitialValues.objectType = _.find(BacNetTypes, item => item.type === objectType);
                InitialValues.propertyId = _.get(object, 'propertyId', '');
                InitialValues.property = _.get(object, 'propertyId', '') === 85 ? 'present' : 'other';
                InitialValues.proprietaryObjectTypeId = proprietaryId === -1 ? '' : proprietaryId;
                InitialValues.arrayIndex = arrayIndex === -1 ? '' : arrayIndex;

                if (_.get(object, 'propertyId', '') === 85) {
                    setPropertySelected(false);
                } else {
                    setPropertySelected(true);
                }

                setOpenArrayIndex(isObjectTypeProprietary(InitialValues.objectType));
                
                InitialValues.showObjectInstance = true;
                InitialValues.showPropertyId = true;
                break;
            default:
                break;
        }

        formik.handleReset(InitialValues);
    };

    const handleObjectTypeChange = event => {
        const { value } = event.target;

        let dataType;

        switch (basType) {
            case 'Niagara':
                dataType = _.chain(NiagaraTypes).find(type => type.type === value.type).get('data').value();
                break;
            case 'BacnetIP':
            case 'BacnetEthernet':
                dataType = _.chain(BacNetTypes).find(type => type.type === value.type).get('data').value();
                break;
            default:
                break;
        }

        switch (value.type) {
            case 'BinaryInput':
            case 'BinaryOutput':
            case 'BinaryValue':
                dataType = 'integer';
                break;
            default:
                break;
        }

        if (dataType !== 'float') {
            if (Intervals[0] !== 0) {
                Intervals.unshift(0);
            }
        } else {
            _.remove(Intervals, value => value === 0);

            formik.setFieldValue('trendInterval', '', false);
        }

        formik.setFieldValue('dataType', dataType, false);
        formik.setFieldValue('objectType', value, false);

        formik.handleChange(event);
    };

    const handlePropertyChange = event => {
        const { value } = event.target;

        if (value === 'other') {
            formik.setFieldValue('propertyId', '', false);
            setPropertySelected(true);
        } else {
            formik.setFieldValue('propertyId', 85, false);
            setPropertySelected(false);
        }

        formik.handleChange(event);
    };

    const handleDataTypeChange = event => {
        const { value } = event.target;

        if (value !== 'float') {
            if (Intervals[0] !== 0) {
                Intervals.unshift(0);
            }
        } else {
            _.remove(Intervals, value => value === 0);

            formik.setFieldValue('trendInterval', '', false);
        }

        formik.handleChange(event);
    };

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        close();
    };

    const renderNiagraFormFields = () => {
        return (
            <React.Fragment>
                <FormControl className={classes.inputWrapper}>
                    <TextField
                        id="obixPath"
                        name="obixPath"
                        type="text"
                        value={formik.values.obixPath}
                        label="oBIX Path"
                        onChange={formik.handleChange}
                        InputLabelProps={{
                            classes: {
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }
                        }}
                        InputProps={{
                            onBlur:  formik.handleBlur,
                            classes: {
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }
                        }}
                        FormHelperTextProps={{
                            classes: {
                                error: classes.error
                            }
                        }}
                        error={Boolean(formik.errors.obixPath) && Boolean(formik.touched.obixPath)}
                        helperText={Boolean(formik.touched.obixPath) && formik.errors.obixPath}
                    />
                </FormControl>
                <FormControl className={classes.inputWrapper}>
                    <InputLabel
                        htmlFor="objectType"
                        classes={{
                            error:   classes.error,
                            focused: classes.labelFocused,
                            root:    classes.label
                        }}
                    >
                        Object Type
                    </InputLabel>
                    <Select
                        value={formik.values.objectType}
                        onChange={handleObjectTypeChange}
                        className={classes.select}
                        inputProps={{
                            name:   'objectType',
                            id:     'objectType',
                            onBlur: formik.handleBlur
                        }}
                    >
                        {
                            NiagaraTypes.map((type, index) => {
                                return (
                                    <MenuItem
                                        key={index}
                                        value={type}
                                    >
                                        {type.type}
                                    </MenuItem>
                                );
                            })
                        }
                    </Select>
                    {formik.touched.objectType &&
                     formik.errors.objectType && <FormHelperText className={classes.error}>{formik.errors.objectType}</FormHelperText>}
                </FormControl>
            </React.Fragment>
        );
    };

    const renderBacNetFormFields = () => {
        return (
            <React.Fragment>
                <FormControl className={classes.inputWrapper}>
                    <InputLabel
                        htmlFor="objectType"
                        classes={{
                            error:   classes.error,
                            focused: classes.labelFocused,
                            root:    classes.label
                        }}
                    >
                        Object Type
                    </InputLabel>
                    <Select
                        value={formik.values.objectType}
                        onChange={handleObjectTypeChange}
                        className={classes.select}
                        inputProps={{
                            name:   'objectType',
                            id:     'objectType',
                            onBlur: formik.handleBlur
                        }}
                    >
                        {
                            BacNetTypes.map((type, index) => {
                                return (
                                    <MenuItem
                                        key={index}
                                        value={type}
                                    >
                                        {type.type}
                                    </MenuItem>
                                );
                            })
                        }
                    </Select>
                    {formik.touched.objectType &&
                     formik.errors.objectType && <FormHelperText className={classes.error}>{formik.errors.objectType}</FormHelperText>}
                </FormControl>
                <FormControl className={classes.inputWrapper}>
                    <TextField
                        id="objectInstance"
                        name="objectInstance"
                        type="text"
                        value={formik.values.objectInstance}
                        label="Object Instance"
                        onChange={formik.handleChange}
                        InputLabelProps={{
                            classes: {
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }
                        }}
                        InputProps={{
                            onBlur:  formik.handleBlur,
                            classes: {
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }
                        }}
                        FormHelperTextProps={{
                            classes: {
                                error: classes.error
                            }
                        }}
                        error={Boolean(formik.errors.objectInstance) && Boolean(formik.touched.objectInstance)}
                        helperText={Boolean(formik.touched.objectInstance) && formik.errors.objectInstance}
                    />
                </FormControl>
                <FormControl className={classes.inputWrapper}>
                    <InputLabel
                        htmlFor="property"
                        classes={{
                            error:   classes.error,
                            focused: classes.labelFocused,
                            root:    classes.label
                        }}
                    >
                        Property
                    </InputLabel>
                    <Select
                        value={formik.values.property}
                        onChange={handlePropertyChange}
                        className={classes.select}
                        inputProps={{
                            name:   'property',
                            id:     'property',
                            onBlur: formik.handleBlur
                        }}
                    >
                        <MenuItem
                            value="present"
                        >
                            Present Value
                        </MenuItem>
                        <MenuItem
                            value="other"
                        >
                            Other
                        </MenuItem>
                    </Select>
                    {formik.touched.property &&
                     formik.errors.property && <FormHelperText className={classes.error}>{formik.errors.property}</FormHelperText>}
                </FormControl>
                <FormControl className={classes.inputWrapper}>
                    <TextField
                        disabled={!propertySelected}
                        id="propertyId"
                        name="propertyId"
                        type="text"
                        value={formik.values.propertyId}
                        label="Property ID"
                        onChange={formik.handleChange}
                        InputLabelProps={{
                            classes: {
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }
                        }}
                        InputProps={{
                            onBlur:  formik.handleBlur,
                            classes: {
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }
                        }}
                        FormHelperTextProps={{
                            classes: {
                                error: classes.error
                            }
                        }}
                        error={Boolean(formik.errors.propertyId) && Boolean(formik.touched.propertyId)}
                        helperText={Boolean(formik.touched.propertyId) && formik.errors.propertyId}
                    />
                </FormControl>
                <FormControl className={classes.inputWrapper}>
                    <TextField
                        disabled={!openArrayIndex}
                        id="arrayIndex"
                        name="arrayIndex"
                        type="string"
                        value={formik.values.arrayIndex}
                        label="Array Index"
                        onChange={formik.handleChange}
                        InputLabelProps={{
                            classes: {
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }
                        }}
                        InputProps={{
                            onBlur:  formik.handleBlur,
                            classes: {
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }
                        }}
                        FormHelperTextProps={{
                            classes: {
                                error: classes.error
                            }
                        }}
                        error={Boolean(formik.errors.arrayIndex) && Boolean(formik.touched.arrayIndex)}
                        helperText={Boolean(formik.touched.arrayIndex) && formik.errors.arrayIndex}
                    />
                </FormControl>
            </React.Fragment>
        );
    };

    const renderSpecificFormFields = () => {
        switch (basType) {
            case 'Niagara':
                return renderNiagraFormFields();
            case 'BacnetIP':
            case 'BacnetEthernet':
                return renderBacNetFormFields();
            default:
                break;
        }
    };

    const getTrendIntervals = () => {
        let data = [];

        Intervals.forEach(interval => {
            const label = interval === 0 ? 'CoV' : `${interval} min`;
            let isBooleanType = formik.values.dataType === 'boolean';

            // remove CoV option for Boolean Data Type
            if (isBooleanType) {
                if (label !== 'CoV') {
                    data.push (
                        <MenuItem
                            key={interval}
                            value={interval}
                        >
                            {label}
                        </MenuItem>
                    );
                }
            } else {
                data.push (
                    <MenuItem
                        key={interval}
                        value={interval}
                    >
                        {label}
                    </MenuItem>
                );
            }
        });

        return data;
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Update"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/editProfileObjectColor.svg" alt="new profile object icon" />
                        <span>New Profile Object</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Select a unique name.
                        </p>
                        {
                            basType === 'Niagara' &&
                            <p>
                                Provide the oBIX Path.
                            </p>
                        }
                        {
                            (basType === 'BacnetIP' || basType === 'BacnetEthernet') &&
                            <React.Fragment>
                                <p>
                                    Select the BACnet Object Type. If you select a Proprietary Object Type you will need to provide the Proprietary Object Type ID.
                                </p>
                                <p>
                                    Provide the BACnet Object Instance.
                                </p>
                                <p>
                                    Select the BACnet Property. Most BACnet Objects you will be interested in will be Present Value. If you select Present Value, we'll fill in the Property ID with 85, if you select Other you'll need to provide the BACnet Object Property ID.
                                </p>
                                <p>
                                    The Array Index is only required for Proprietary Objects. If you have a Proprietary Object that does not have an Array Index, you can leave the field blank.
                                </p>
                                <p>
                                    Select the Data Type of the BACnet Object.
                                </p>
                            </React.Fragment>
                        }
                        <p>Object type is required</p>
                        <p>
                            Select a trend interval.
                        </p>
                        <p>
                            Note that changes made to a Profile will update all Devices using this Profile. Please double check your work before making any changes.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        {
                            renderSpecificFormFields()
                        }
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="dataType"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                            >
                                Data Type
                            </InputLabel>
                            <Select
                                value={formik.values.dataType}
                                onChange={handleDataTypeChange}
                                className={classes.select}
                                inputProps={{
                                    name:   'dataType',
                                    id:     'dataType',
                                    onBlur: formik.handleBlur
                                }}
                            >
                                <MenuItem
                                    value="boolean"
                                >
                                    Boolean
                                </MenuItem>
                                <MenuItem
                                    value="float"
                                >
                                    Float
                                </MenuItem>
                                <MenuItem
                                    value="integer"
                                >
                                    Integer
                                </MenuItem>
                                <MenuItem
                                    value="text"
                                >
                                    Text
                                </MenuItem>
                            </Select>
                            {formik.touched.dataType &&
                             formik.errors.dataType && <FormHelperText className={classes.error}>{formik.errors.dataType}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="trendInterval"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                            >
                                Trend Interval
                            </InputLabel>
                            <Select
                                value={formik.values.trendInterval}
                                onChange={formik.handleChange}
                                className={classes.select}
                                inputProps={{
                                    name:   'trendInterval',
                                    id:     'trendInterval',
                                    onBlur: formik.handleBlur
                                }}
                            >
                                { getTrendIntervals() }
                            </Select>
                            {formik.touched.trendInterval &&
                             formik.errors.trendInterval && <FormHelperText className={classes.error}>{formik.errors.trendInterval}</FormHelperText>}
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

EditProfileObjectModal.defaultProps = {
    close: () => {},
    open:  false,
    title: ''
};

EditProfileObjectModal.propTypes = {
    close: func,
    open:  bool,
    title: string
};

const mapStateToProps = state => {
    return {
        errors:            state.messages.errors,
        isLoading:         state.ui.isLoading,
        profiles:          state.profile.profiles,
        selectedProfileId: state.profile.selectedProfileId
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...ProfileObjectActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(EditProfileObjectModal);
